import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldCheck, Receipt as ReceiptIcon, FileCheck2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const INPUT = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const money = (c, v) => `${c || ""} ${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const PCT = { "50:50": 50, "80:20": 80, "100_prepaid": 100 };
const badge = (s) => s === "issued" || s === "confirmed"
  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
  : "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400";

const Row = ({ label, children, testid }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2 last:border-0 dark:border-ink-line">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className="text-right font-medium text-slate-800 dark:text-ink-text" data-testid={testid}>{children}</span>
  </div>
);

export default function ProformaInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [pi, setPi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // "verify" | "receipt"

  const load = useCallback(async () => {
    try { setPi((await api.get(`/finance/proforma_invoice/${id}`)).data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const issuePI = async () => {
    setBusy(true);
    try { await api.post(`/finance/proforma_invoice/${id}/issue`, {}); toast.success("Pro Forma Invoice issued"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const issueReceipt = async (rid) => {
    setBusy(true);
    try { await api.post(`/finance/payment_receipt/${rid}/issue`, {}); toast.success("Receipt issued"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const createInvoice = async () => {
    setBusy(true);
    try { const { data } = await api.post("/finance/invoice", { proforma_id: id }); toast.success("Invoice draft created"); navigate(`/finance/invoices?focus=${data.id}`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>;
  if (!pi) return null;

  const total = Number(pi.total_amount || 0);
  const pct = PCT[pi.payment_terms] || 0;
  const required = (total * pct) / 100;
  const rel = pi.related || {};

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="pi-detail-page">
      <button onClick={() => navigate("/finance/proforma-invoices")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-ink-text">
        <ArrowLeft className="h-4 w-4" /> Pro Forma Invoices
      </button>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Proforma Invoice</p>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text" data-testid="pi-number">{pi.document_number || "— Draft —"}</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${badge(pi.status)}`} data-testid="pi-status">{pi.status}</span>
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${pi.payment_verified ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"}`} data-testid="pi-payment-status">
              {pi.payment_verified ? "Payment Verified" : "Payment Pending"}
            </span>
          </div>
        </div>

        <div className="mt-5 text-sm">
          <Row label="Customer" testid="pi-customer">{pi.customer_name || "—"}</Row>
          <Row label="Quotation">{pi.quotation_number || "—"}</Row>
          <Row label="Amount" testid="pi-amount">{money(pi.currency, pi.total_amount)}</Row>
          <Row label="Payment terms" testid="pi-terms">{pi.payment_terms}</Row>
          <Row label={`Required payment (${pct}%)`}>{money(pi.currency, required)}</Row>
          <Row label="Booking #" testid="pi-booking">{pi.booking_number || "— not yet born —"}</Row>
          {pi.payment_verification && (
            <Row label="Confirmed amount" testid="pi-confirmed">{money(pi.currency, pi.payment_verification.confirmed_amount)}</Row>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {pi.status === "draft" && hasPermission("proformas.issue") && (
            <button onClick={issuePI} disabled={busy} data-testid="pi-issue-button"
              className="inline-flex items-center gap-2 rounded-lg bg-ink-gold px-4 py-2 text-sm font-semibold text-[#0B1120] disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Issue PI
            </button>
          )}
          {pi.status === "issued" && !pi.payment_verified && hasPermission("payments.verify") && (
            <button onClick={() => setModal("verify")} data-testid="pi-verify-button"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500">
              <ShieldCheck className="h-4 w-4" /> Verify Payment
            </button>
          )}
          {pi.status === "issued" && !pi.payment_verified && !hasPermission("payments.verify") && (
            <span className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-500 dark:bg-ink-panel dark:text-slate-400" data-testid="pi-verify-forbidden">
              Payment verification requires Finance/Manager authority.
            </span>
          )}
          {pi.payment_verified && hasPermission("payments.create") && (
            <button onClick={() => setModal("receipt")} data-testid="pi-create-receipt-button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-ink-line dark:text-ink-text dark:hover:bg-ink-panel">
              <ReceiptIcon className="h-4 w-4" /> Create Deposit Receipt
            </button>
          )}
          {pi.payment_verified && hasPermission("invoices.create") && (
            <button onClick={createInvoice} disabled={busy} data-testid="pi-create-invoice-button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-ink-line dark:text-ink-text dark:hover:bg-ink-panel">
              <FileCheck2 className="h-4 w-4" /> Create Invoice
            </button>
          )}
        </div>
      </div>

      {/* Related records */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="pi-related">
        <h2 className="mb-3 font-display text-sm font-bold uppercase tracking-wider text-slate-500">Related Records</h2>
        <div className="space-y-4 text-sm">
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-400">Booking</p>
            {rel.booking ? (
              <button onClick={() => navigate("/finance/bookings")} className="font-mono-tf text-ink-gold hover:underline" data-testid="pi-related-booking">
                {rel.booking.booking_number} · {rel.booking.status}
              </button>
            ) : <p className="text-slate-400">No Booking yet — verify payment to give birth to the Booking.</p>}
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-400">Deposit Receipts</p>
            {(rel.deposit_receipts || []).length === 0 ? <p className="text-slate-400">None</p> : (
              <ul className="space-y-1" data-testid="pi-deposit-receipts">
                {rel.deposit_receipts.map((r) => (
                  <li key={r.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 dark:bg-ink-panel/40">
                    <span className="font-mono-tf text-xs text-slate-700 dark:text-slate-200">{r.document_number || "— draft —"} · {money(r.currency, r.paid_amount)}</span>
                    <span className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge(r.status)}`}>{r.status}</span>
                      {r.status === "draft" && hasPermission("payments.create") && (
                        <button onClick={() => issueReceipt(r.id)} disabled={busy} data-testid={`receipt-issue-${r.id}`} className="rounded-md bg-ink-gold px-2 py-1 text-[10px] font-semibold text-[#0B1120] disabled:opacity-50">Issue</button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-400">Invoices</p>
            {(rel.invoices || []).length === 0 ? <p className="text-slate-400">None</p> : (
              <ul className="space-y-1">
                {rel.invoices.map((i) => (
                  <li key={i.id}><button onClick={() => navigate("/finance/invoices")} className="font-mono-tf text-xs text-ink-gold hover:underline">{i.document_number || "— draft —"} · {money(i.currency, i.total_amount)} · {i.status}</button></li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {modal === "verify" && (
        <VerifyModal pi={pi} required={required} onClose={() => setModal(null)} onDone={async () => { setModal(null); await load(); }} />
      )}
      {modal === "receipt" && (
        <ReceiptModal pi={pi} onClose={() => setModal(null)} onDone={async () => { setModal(null); await load(); }} />
      )}
    </div>
  );
}

function VerifyModal({ pi, required, onClose, onDone }) {
  const [confirmed, setConfirmed] = useState(""); // MUST be entered manually — never prefilled
  const [ref, setRef] = useState(""); const [method, setMethod] = useState(""); const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!confirmed) { toast.error("Enter the confirmed amount"); return; }
    setSaving(true);
    try {
      await api.post(`/finance/proforma_invoice/${pi.id}/verify-payment`, {
        confirmed_amount: String(confirmed), payment_reference: ref || undefined,
        payment_method: method || undefined, notes: notes || undefined,
      });
      toast.success("Payment verified — Booking created");
      await onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="verify-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900 dark:text-ink-text">Verify Payment</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">This is an explicit human financial confirmation. Enter the amount you have actually received.</p>
        <div className="mb-4 rounded-lg bg-slate-50 p-3 text-sm dark:bg-ink-panel/40">
          <div className="flex justify-between"><span className="text-slate-500">Payment terms</span><span className="font-medium text-slate-800 dark:text-ink-text">{pi.payment_terms}</span></div>
          <div className="mt-1 flex justify-between"><span className="text-slate-500">Required payment</span><span className="font-mono-tf font-semibold text-slate-800 dark:text-ink-text" data-testid="verify-required-amount">{money(pi.currency, required)}</span></div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Confirmed amount ({pi.currency}) *</label>
            <input type="number" value={confirmed} onChange={(e) => setConfirmed(e.target.value)} placeholder="Enter received amount" data-testid="verify-confirmed-input" className={INPUT} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Payment reference</label><input value={ref} onChange={(e) => setRef(e.target.value)} data-testid="verify-reference-input" className={INPUT} /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Payment method</label><input value={method} onChange={(e) => setMethod(e.target.value)} data-testid="verify-method-input" className={INPUT} /></div>
          </div>
          <div><label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Notes</label><input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="verify-notes-input" className={INPUT} /></div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 dark:border-ink-line dark:text-slate-300">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="verify-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Confirm Payment
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiptModal({ pi, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [ref, setRef] = useState(""); const [method, setMethod] = useState(""); const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!amount) { toast.error("Enter the receipt amount"); return; }
    setSaving(true);
    try {
      await api.post("/finance/payment_receipt", {
        source_document_type: "proforma_invoice", source_document_id: pi.id,
        receipt_kind: "deposit", paid_amount: String(amount), currency: pi.currency,
        payment_reference: ref || undefined, payment_method: method || undefined, notes: notes || undefined,
      });
      toast.success("Deposit receipt draft created");
      await onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="receipt-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900 dark:text-ink-text">Deposit Receipt</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">Deposit Receipt #1 references the PI and does not require a Booking Number.</p>
        <div className="space-y-3">
          <div><label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Amount ({pi.currency}) *</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="receipt-amount-input" className={INPUT} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Payment reference</label><input value={ref} onChange={(e) => setRef(e.target.value)} data-testid="receipt-reference-input" className={INPUT} /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Payment method</label><input value={method} onChange={(e) => setMethod(e.target.value)} data-testid="receipt-method-input" className={INPUT} /></div>
          </div>
          <div><label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Notes</label><input value={notes} onChange={(e) => setNotes(e.target.value)} className={INPUT} /></div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 dark:border-ink-line dark:text-slate-300">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="receipt-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-ink-gold px-4 py-2 text-sm font-semibold text-[#0B1120] disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Receipt
          </button>
        </div>
      </div>
    </div>
  );
}
